/**
 * Created by MateuszWaligora on 17.07.2017.
 */

import Vue from 'vue';
import {HTTPCODES} from '../../plugins/types';
/**
 * @module Store/prices
 * @description Module for prices
 */

/**
 * @constant State of price store
 * @type {Object}
 * @property {Object} prices - object holding prices for products. id of a price is a key in the object.
 * @description This constant holds the state of product store
 * */
const state = {
    prices: {}
};

/**
 * @constant Getters
 * @type {Object}
 * @property {function} getAllPrices - returns prices object
 * @description This constant represents the getters for the price store
 * */
const getters = {
    getAllPrices: state => state.prices
};

/**
 * @constant Mutations
 * @type {Object}
 * @property {function} updateProductGroupList - updates product groups list
 * @property {function} updatePrices - updates prices in the store
 * @description This constant represents the mutations for the product store
 * */
const mutations = {
    updatePrices: (state, prices) => state.prices = prices
};

/**
 * @constant Actions
 * @type {Object}
 * @property {function} populatePricesAsync - async action that fetches the prices from the server
 * @description This constant represents the actions for the product store
 * */
const actions = {
    async addPriceAsync ({rootState, dispatch, commit}, {productId, price, disableFetch}) {
        let body = productId + ' ' + price;
        try {
            let {data} = await Vue.axios.post(`${rootState.config.priceServicePort}/price`,
                body,
                {
                    headers: {
                        'Content-type': 'text/plain'
                    }
                });
            if(!disableFetch) {
                dispatch('getProductListAsync');
                dispatch('getProductGroupListAsync');
                dispatch('getPricesAsync');
            }
            return {data};
        } catch (err) {
            if(err.response) {
                if(err.response.status === 401) {
                    commit('addAlert', {
                        title: 'Unauthorized ',
                        type: 'danger',
                        body: 'to add price'
                    });
                    return {err: HTTPCODES[err.response.status]};
                }
            }
            commit('addAlert', {
                title: 'Failed ',
                type: 'danger',
                body: `to add price ${price.id}`
            });
            throw new Error(err);
        }
    },
    async updatePriceAsync ({rootState, commit, dispatch}, {price, disableFetch}) {
        try {
            let response = await Vue.axios.put(`${rootState.config.priceServicePort}/price/${price.id}`,
                {
                    ...price,
                    price: `${price.price}`
                },
                {
                    headers: {
                        'Content-type': 'application/json'
                    }
                });
            if(!disableFetch) {
                dispatch('getPricesAsync');
                dispatch('getProductListAsync');
            }
            return response;
        } catch (err) {
            if(err.response && err.response.status === 401) {
                commit('addAlert', {
                    title: 'Unauthorized ',
                    type: 'danger',
                    body: `to update price ${price.id}`
                });
                throw Error(HTTPCODES[err.response.status]);
            }
            commit('addAlert', {
                title: 'Failed ',
                type: 'danger',
                body: `to update price ${price.id}`
            });
            throw new Error(err);
        }
    },

    /**
     * @function getPricesAsync
     * @description async action that fetches the price list from the server for chosen product group
     * @returns {Object} prices or error object
     * */
    async getPricesAsync ({commit, rootState}) {
        try {
            let {data} = await Vue.axios.get(`${rootState.config.priceServicePort}/price`);
            let priceList = {};
            if(data) {
                data.filter(item => item).forEach(item => {
                    priceList[item.product] = item;
                });
            }
            commit('updatePrices', priceList);
            return priceList;
        } catch (err) {
            if(err.response && err.response.status >= 400) {
                if(err.response.status === 401) {
                    return {err: HTTPCODES[err.response.status]};
                }
            }
            commit('updatePrices', {});
            commit('addAlert', {
                title: 'Failed ',
                type: 'danger',
                body: 'to get prices collection'
            });
            throw new Error(err);
        }
    },


    /**
     * @function saveCompanyData
     * @description saves company data on the server
     * @returns {Object} response or error object
     */
    async saveCompanyData ({commit}, {company, address, USID}) {
        let body = {
            company: company,
            address: `${address.street} ${address.streetNumber} <br> ${address.postCode} ${address.city}`,
            USID: USID
        };
        try {
            let res = await Vue.axios.put('/prices/basket', body);
            return res;
        } catch (err) {
            if(err.response && err.response.status >= 400) {
                if(err.response.status === 401) {
                    commit('addAlert', {
                        title: 'Unauthorized ',
                        type: 'danger',
                        body: 'to save company data'
                    });
                    return {err: HTTPCODES[err.response.status]};
                }
            }
            commit('addAlert', {
                title: 'Failed ',
                type: 'danger',
                body: 'to save company data'
            });
            throw new Error(err);
        }
    },
    /**
     * @function getCompanyData
     * @description saves company data on the server
     * @returns {Object} response or error object
     */
    async getCompanyData ({commit}) {
        try {
            let {data} = await Vue.axios.get('/prices/basket');
            return data || {};
        } catch (err) {
            if(err.response && err.response.status >= 400) {
                if(err.response.status === 401) {
                    commit('addAlert', {
                        title: 'Unauthorized ',
                        type: 'danger',
                        body: 'to get company data'
                    });
                    return {err: HTTPCODES[err.response.status]};
                }
            }
            commit('addAlert', {
                title: 'Failed ',
                type: 'danger',
                body: 'to get company data'
            });
            throw new Error(err);
        }
    },

    /**
     * @function deletePriceAsync
     * @description async action that deletes the price from the server
     * @param {String} id - price id
     * @returns {Object} response or error object
     * */
    async deletePriceAsync ({rootState, commit}, id) {
        try {
            await Vue.axios.delete(`${rootState.config.priceServicePort}/price/${id}`);
            return id;
        } catch (err) {
            if(err.response && err.response.status >= 400) {
                if(err.response.status === 401) {
                    commit('addAlert', {
                        title: 'Unauthorized ',
                        type: 'danger',
                        body: `to delete price ${id}`
                    });
                    return {err: HTTPCODES[err.response.status]};
                }
            }
            commit('addAlert', {
                title: 'Failed ',
                type: 'danger',
                body: `to delete price ${id}`
            });
            throw new Error(err);
        }
    }
};

export default {
    state,
    getters,
    mutations,
    actions
};
